import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { useAuth } from '../../context/AuthContext';
import server from '../../global/server';
import ResumoRepasse from '../../components/ResumoRepasse';
import { useApp } from '../../context/AppContext';
import LoadingButton from '@mui/lab/LoadingButton';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import useHandleErrors from '../../hooks/useHandleErrors';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogContent,
    FormControl,
    InputAdornment,
    Link,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SelectTopics from '../../components/SelectTopics';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { DateCalendar, PickersDay } from '@mui/x-date-pickers';
import DateInput from '../../components/DateInput/Index';
import { criarDataDeISO } from '../../global/dataDigitada';
import {
    differenceInMonths,
    isBefore,
    isSameDay,
    parseISO,
    setHours,
    setMinutes,
    startOfDay,
} from 'date-fns';
import lodash from 'lodash';
import { useFetch } from '../../hooks/useFetch';
import ConflictDialog from '../../components/ConflictDialog';
import { precoMinimo } from '../../global/repasse';

const SectionCard = ({ children }) => (
    <Box sx={{
        width: '100%',
        bgcolor: 'white',
        borderRadius: '16px',
        border: '1px solid #e8e8e8',
        p: { xs: '20px 16px', sm: '28px 24px' },
    }}>
        {children}
    </Box>
);

// Junta a data escolhida com o horario digitado (HH:mm) no prazo do sorteio
const montarPrazo = (data, hora) => {
    if (!data || !hora) return null;

    const [h, m] = String(hora).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;

    const prazo = new Date(data);
    prazo.setHours(h, m, 0, 0);
    return prazo;
};

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle1" fontWeight={600} color="#333" sx={{ mb: 2.5 }}>
        {children}
    </Typography>
);

export default () => {
    const { user, rules } = useAuth();
    const { isOffline } = useApp();
    const [hasError, setError] = useState();
    useHandleErrors(hasError);

    const isApproved = user?.profile?.approved;
    const [showSuccess, setShowSuccess] = useState(false);

    const [isFreeEvent, setIsFreeEvent] = useState(false);
    const [previousPricing, setPreviousPricing] = useState(0);
    const [pricingDisplay, setPricingDisplay] = useState('0,00');
    const [timeInput, setTimeInput] = useState('');
    const [currentDate, setCurrentDate] = useState(null);

    // Mascara de valor estilo banco: usuario digita so numeros, centavos sao automaticos
    const handlePricingChange = (e) => {
        let raw = e.target.value.replace(/\D/g, '');
        if (raw === '') raw = '0';
        const cents = parseInt(raw, 10);
        const reais = cents / 100;
        const formatted = reais.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setPricingDisplay(formatted);
        formik.setFieldValue('pricing', reais);
    };

    const navigation = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    const [conflictData, setConflictData] = useState(null);
    const [conflictOpen, setConflictOpen] = useState(false);
    const [pendingScheduleISO, setPendingScheduleISO] = useState(null);

    // Buscar encontros existentes do anfitriao para verificar conflitos
    const { data: allMeetings } = useFetch('/profile/meeting');

    const formik = useFormik({
        initialValues: {
            title: '',
            description: '',
            status: true,
            visible_public: false,
            raffle: false,
            pricing: 0.0,
            topics: [],
            meeting_schedule: [],
            // Prazo do sorteio automatico, so usado na modalidade sorteio
            raffle_deadline_on: false,
            raffle_date: null,
            raffle_time: '',
        },
        validationSchema: Yup.object().shape({
            title: Yup.string().required('Campo obrigatório').max(40, 'Máximo 40 caracteres'),
            description: Yup.string().required('Campo obrigatório').max(350, 'Máximo 350 caracteres'),
            pricing: isFreeEvent
                ? Yup.mixed().notRequired()
                : Yup.number('Use ponto para delimitar')
                    .min(precoMinimo(rules), `Preco minimo R$ ${Number(precoMinimo(rules)).toFixed(2).replace('.', ',')}`)
                    .required('Campo obrigatório'),
            meeting_schedule: Yup.array().min(1, 'Adicione pelo menos um horário'),
        }),
        validateOnMount: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                setSubmitting(true);

                const ehSorteio = values.raffle === true || values.raffle === 'true';
                const prazoDoSorteio = ehSorteio && values.raffle_deadline_on
                    ? montarPrazo(values.raffle_date, values.raffle_time)
                    : null;

                if (ehSorteio && values.raffle_deadline_on && !prazoDoSorteio) {
                    setSubmitting(false);
                    enqueueSnackbar('Escolha a data e o horário limite do sorteio', { variant: 'warning' });
                    return;
                }

                const payload = {
                    ...values,
                    pricing: isFreeEvent ? 0.0 : values.pricing,
                    free: isFreeEvent,
                    visible_public: values.visible_public === true || values.visible_public === 'true',
                    raffle: ehSorteio,
                    raffle_at: prazoDoSorteio ? prazoDoSorteio.toISOString() : null,
                };
                delete payload.raffle_deadline_on;
                delete payload.raffle_date;
                delete payload.raffle_time;

                await server.post('/profile/meeting', payload);
                setShowSuccess(true);
            } catch (error) {
                setSubmitting(false);
                setError(error);
            }
        },
    });

    const isRaffle = formik.values.raffle === true || formik.values.raffle === 'true';
    const scheduledDates = formik.values.meeting_schedule || [];

    // Recriar encontro encerrado: copia tudo menos os horarios, que sao novos.
    // O encontro original continua intacto entre os encerrados.
    const [searchParams] = useSearchParams();
    const idParaRecriar = searchParams.get('recriar');
    const [jaPreencheu, setJaPreencheu] = useState(false);

    useEffect(() => {
        if (!idParaRecriar || jaPreencheu || !Array.isArray(allMeetings)) return;

        const original = allMeetings.find(m => String(m.id) === String(idParaRecriar));
        if (!original) return;

        formik.setValues({
            title: original.title || '',
            description: original.description || '',
            status: true,
            visible_public: Boolean(original.visible_public),
            raffle: Boolean(original.raffle),
            pricing: Number(original.pricing) || 0.0,
            topics: (original.topics || []).map(t => t.topic_id ?? t.Topic?.id).filter(Boolean),
            meeting_schedule: [],
            raffle_deadline_on: false,
            raffle_date: null,
            raffle_time: '',
        });

        if (original.free) setIsFreeEvent(true);
        const valor = Number(original.pricing) || 0;
        setPreviousPricing(valor);
        setPricingDisplay(valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
        setJaPreencheu(true);
    }, [idParaRecriar, allMeetings, jaPreencheu]);

    // Pricing reset quando gratuito
    useEffect(() => {
        if (isFreeEvent) {
            setPreviousPricing(formik.values.pricing);
            formik.setFieldValue('pricing', 0.0);
            setPricingDisplay('0,00');
        } else {
            formik.setFieldValue('pricing', previousPricing);
            const formatted = previousPricing.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            setPricingDisplay(formatted);
        }
    }, [isFreeEvent]);

    // Sorteio nao pode ser gratuito + sorteio sempre publico
    useEffect(() => {
        if (isRaffle) {
            formik.setFieldValue('visible_public', true);
            if (isFreeEvent) setIsFreeEvent(false);
        } else {
            // Trocou para agendamento: o prazo do sorteio deixa de existir
            formik.setFieldValue('raffle_deadline_on', false);
            formik.setFieldValue('raffle_date', null);
            formik.setFieldValue('raffle_time', '');
        }
    }, [isRaffle]);

    // Horarios de outros encontros para verificar conflitos
    const otherMeetingsSchedules = (Array.isArray(allMeetings) ? allMeetings : [])
        .flatMap(m => (m.meeting_schedule || []).map(s => ({
            datetime: s.datetime,
            meetingTitle: m.title,
        })))
        .filter(s => Boolean(s.datetime) && isBefore(new Date(), parseISO(s.datetime)));

    // Dias que ja tem horarios selecionados (para destacar no calendario)
    const scheduledDays = lodash.uniq(
        scheduledDates.map(dt => startOfDay(new Date(dt)).toISOString())
    );

    const finalizeAddSchedule = (iso) => {
        formik.setFieldValue('meeting_schedule', lodash.uniq([...scheduledDates, iso]));
        setTimeInput('');
        setPendingScheduleISO(null);
    };

    const addSchedule = async () => {
        if (!currentDate) {
            enqueueSnackbar('Selecione uma data no calendário', { variant: 'warning' });
            return;
        }
        const [h, m] = timeInput.split(':').map(Number);
        if (isNaN(h) || isNaN(m)) {
            enqueueSnackbar('Horário inválido', { variant: 'warning' });
            return;
        }

        const selected = setMinutes(setHours(currentDate, h), m);
        const selectedISO = selected.toISOString();

        // Verifica se o horario e pelo menos 10 minutos no futuro
        const minTime = new Date(Date.now() + 10 * 60 * 1000);
        if (isBefore(selected, minTime)) {
            enqueueSnackbar('O horário deve ser pelo menos 10 minutos a frente do horário atual', { variant: 'warning' });
            return;
        }

        // Conflito com horarios deste encontro (min 1h)
        const conflictSelf = scheduledDates.some(s => {
            return Math.abs(new Date(s).getTime() - selected.getTime()) < 60 * 60 * 1000;
        });
        if (conflictSelf) {
            enqueueSnackbar('Mínimo 1 hora de intervalo entre horários', { variant: 'warning' });
            return;
        }

        // Conflito com outros encontros criados (verificacao local)
        const conflictOther = otherMeetingsSchedules.find(s => {
            return Math.abs(new Date(s.datetime).getTime() - selected.getTime()) < 60 * 60 * 1000;
        });
        if (conflictOther) {
            const hora = new Date(conflictOther.datetime).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
            enqueueSnackbar(`Conflito com "${conflictOther.meetingTitle}" as ${hora}. Minimo 1h de intervalo.`, { variant: 'warning' });
            return;
        }

        // Verificar conflito com encontros comprados como conviva (via API)
        try {
            const { data: conflictCheck } = await server.get('/booking/conflicts', {
                params: { datetime: selectedISO },
            });
            if (conflictCheck.hasConflict) {
                if (conflictCheck.asHost?.length > 0) {
                    // Ja verificado localmente, mas safety net
                    enqueueSnackbar('Você já tem um encontro criado neste horário', { variant: 'warning' });
                    return;
                }
                if (conflictCheck.asConviva?.length > 0) {
                    setPendingScheduleISO(selectedISO);
                    setConflictData(conflictCheck);
                    setConflictOpen(true);
                    return;
                }
            }
        } catch (err) {
            // Se falhar a verificacao, prosseguir (backend valida no submit)
            console.warn('Erro ao verificar conflitos:', err);
        }

        finalizeAddSchedule(selectedISO);
    };

    // Horarios do dia selecionado
    const currentDaySchedules = scheduledDates
        .filter(dt => currentDate && isSameDay(new Date(dt), currentDate))
        .sort((a, b) => new Date(a) - new Date(b));

    // Total de horarios
    const totalSchedules = scheduledDates.length;

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            maxWidth: 720,
            mx: 'auto',
            p: { xs: '16px 12px 32px', sm: '24px 16px 48px' },
            gap: 3,
        }}>
            {/* Header */}
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, pt: 1 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: '#006a61' }}>
                    <AddCircleOutlineIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={600}>
                    Novo encontro
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Preencha todos os dados para criar seu encontro
                </Typography>
            </Box>

            {!isApproved && (
                <Alert severity="warning" sx={{ width: '100%', borderRadius: '12px', '& .MuiAlert-message': { fontSize: '0.85rem' } }}>
                    Seu cadastro de anfitrião está em análise. Você pode criar encontros normalmente, mas eles só ficarão visíveis para outros usuários após a aprovação do seu cadastro.
                </Alert>
            )}

            <form onSubmit={formik.handleSubmit} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 24 }}>

                {/* Informacoes basicas */}
                <SectionCard>
                    <SectionTitle>Informações do encontro</SectionTitle>
                    <Stack spacing={2}>
                        <TextField
                            label="Título do encontro"
                            name="title"
                            size="small"
                            fullWidth
                            required
                            value={formik.values.title}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={isOffline}
                            error={formik.touched.title && Boolean(formik.errors.title)}
                            helperText={formik.touched.title ? formik.errors.title : `${40 - (formik.values.title?.length || 0)} caracteres restantes`}
                            inputProps={{ maxLength: 40 }}
                        />
                        <TextField
                            label="Descrição do encontro"
                            name="description"
                            size="small"
                            fullWidth
                            required
                            multiline
                            minRows={3}
                            value={formik.values.description}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={isOffline}
                            error={formik.touched.description && Boolean(formik.errors.description)}
                            helperText={formik.touched.description ? formik.errors.description : `${350 - (formik.values.description?.length || 0)} caracteres restantes`}
                            inputProps={{ maxLength: 350 }}
                        />
                        <SelectTopics
                            onChange={formik.setFieldValue}
                            name="topics"
                            value={formik.values.topics}
                        />
                    </Stack>
                </SectionCard>

                {/* Tipo e visibilidade */}
                <SectionCard>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2.5 }}>
                        <Typography variant="subtitle1" fontWeight={600} color="#333">
                            Tipo e visibilidade
                        </Typography>
                        <Tooltip
                            arrow
                            title={
                                <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" fontWeight={600} display="block" sx={{ mb: 0.5 }}>Público</Typography>
                                    <Typography variant="caption" display="block">Qualquer usuário pode encontrar e comprar. Conta com transmissão ao vivo durante o encontro.</Typography>
                                    <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 1, mb: 0.5 }}>Privado</Typography>
                                    <Typography variant="caption" display="block">Qualquer usuário pode encontrar e comprar, porém o encontro não terá transmissão ao vivo.</Typography>
                                </Box>
                            }
                        >
                            <HelpOutlineIcon sx={{ fontSize: 18, color: '#999', cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                    <Stack spacing={2.5}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Modalidade</Typography>
                            <ToggleButtonGroup
                                value={isRaffle ? 'raffle' : 'schedule'}
                                exclusive
                                onChange={(e, val) => { if (val) formik.setFieldValue('raffle', val === 'raffle'); }}
                                fullWidth
                                size="small"
                                sx={{
                                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 500 },
                                    '& .Mui-selected': { bgcolor: '#e6f3f1 !important', color: '#006a61 !important', borderColor: '#006a61 !important' },
                                }}
                            >
                                <ToggleButton value="schedule">Agendamento</ToggleButton>
                                <ToggleButton value="raffle">Sorteio</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {/* Prazo do sorteio: so aparece na modalidade sorteio e so
                            abre os campos quando o anfitriao liga o interruptor */}
                        {isRaffle && (
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Definir data e horário limite do sorteio?
                                    </Typography>
                                    <Switch
                                        checked={Boolean(formik.values.raffle_deadline_on)}
                                        onChange={(e) => formik.setFieldValue('raffle_deadline_on', e.target.checked)}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#006a61' },
                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#006a61' },
                                        }}
                                    />
                                </Box>

                                <Collapse in={Boolean(formik.values.raffle_deadline_on)} unmountOnExit>
                                    <Stack spacing={2} sx={{ mt: 1.5 }}>
                                        <DateInput
                                            label="Data do sorteio"
                                            value={formik.values.raffle_date}
                                            onChange={iso => formik.setFieldValue('raffle_date', iso ? criarDataDeISO(iso) : null)}
                                            minima={new Date()}
                                            rotuloMinima="A data não pode ser no passado"
                                        />
                                        <TextField
                                            type="time"
                                            label="Horário limite"
                                            size="small"
                                            fullWidth
                                            disabled={!formik.values.raffle_date}
                                            value={formik.values.raffle_time}
                                            onChange={(e) => formik.setFieldValue('raffle_time', e.target.value)}
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ step: 60 }}
                                            helperText={
                                                formik.values.raffle_date
                                                    ? 'Passando desse horário sem sorteio manual, o sistema sorteia sozinho'
                                                    : 'Escolha a data para liberar o horário'
                                            }
                                        />
                                    </Stack>
                                </Collapse>

                                {!formik.values.raffle_deadline_on && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                        Sem prazo definido, o sorteio automático acontece 10 minutos antes do encontro.
                                        Você também pode definir isso depois, no acompanhamento do encontro.
                                    </Typography>
                                )}
                            </Box>
                        )}
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Visibilidade</Typography>
                            <ToggleButtonGroup
                                value={formik.values.visible_public === true || formik.values.visible_public === 'true' ? 'public' : 'private'}
                                exclusive
                                onChange={(e, val) => { if (val) formik.setFieldValue('visible_public', val === 'public'); }}
                                fullWidth
                                size="small"
                                disabled={isRaffle}
                                sx={{
                                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 500 },
                                    '& .Mui-selected': { bgcolor: '#e6f3f1 !important', color: '#006a61 !important', borderColor: '#006a61 !important' },
                                }}
                            >
                                <ToggleButton value="public">Público</ToggleButton>
                                <ToggleButton value="private">Privado</ToggleButton>
                            </ToggleButtonGroup>
                            {isRaffle && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Sorteios são sempre públicos
                                </Typography>
                            )}
                        </Box>
                    </Stack>
                </SectionCard>

                {/* Valor */}
                <SectionCard>
                    <SectionTitle>Valor</SectionTitle>
                    <Stack spacing={2}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Encontro gratuito</Typography>
                            <Switch
                                checked={isFreeEvent}
                                onChange={() => setIsFreeEvent(!isFreeEvent)}
                                disabled={isRaffle}
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#006a61' },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#006a61' },
                                }}
                            />
                        </Box>
                        <TextField
                            label="Valor"
                            size="small"
                            fullWidth
                            value={isFreeEvent ? '0,00' : pricingDisplay}
                            onChange={handlePricingChange}
                            onBlur={() => formik.setFieldTouched('pricing', true)}
                            disabled={isFreeEvent || isOffline}
                            error={formik.touched.pricing && Boolean(formik.errors.pricing)}
                            helperText={
                                isFreeEvent
                                    ? 'Este encontro será gratuito'
                                    : formik.touched.pricing && formik.errors.pricing
                                        ? formik.errors.pricing
                                        : `Valor minimo R$ ${Number(precoMinimo(rules)).toFixed(2).replace('.', ',')}`
                            }
                            InputProps={{
                                startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                            }}
                            inputProps={{ inputMode: 'numeric' }}
                        />
                        <ResumoRepasse preco={formik.values.pricing} gratuito={isFreeEvent} />
                    </Stack>
                </SectionCard>

                {/* Agenda */}
                <SectionCard>
                    <SectionTitle>Agenda</SectionTitle>
                    <Stack spacing={2}>
                        <Alert severity="warning" variant="outlined" sx={{ '& .MuiAlert-message': { fontSize: '0.8rem' } }}>
                            Os horários devem ser agendados com pelo menos 10 minutos de antecedência.
                        </Alert>
                        {!currentDate && (
                            <Alert severity="info">
                                Digite a data ou escolha um dia no calendário para adicionar horários
                            </Alert>
                        )}
                        {/* Digitar a data e mais rapido do que navegar o calendario,
                            que fica aqui so como apoio */}
                        <DateInput
                            label="Data"
                            value={currentDate}
                            onChange={iso => setCurrentDate(iso ? criarDataDeISO(iso) : null)}
                            minima={new Date()}
                            rotuloMinima="A data não pode ser no passado"
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <DateCalendar
                                disablePast
                                shouldDisableDate={e => differenceInMonths(e, new Date()) > 0}
                                onChange={day => setCurrentDate(day)}
                                slots={{
                                    day: ({ day, ...rest }) => {
                                        const dayISO = startOfDay(day).toISOString();
                                        const isSelected = currentDate && startOfDay(currentDate).toISOString() === dayISO;
                                        const hasSchedule = scheduledDays.includes(dayISO);

                                        return (
                                            <PickersDay
                                                {...rest}
                                                day={day}
                                                sx={{
                                                    bgcolor: isSelected ? '#006a61' : hasSchedule ? '#e6f3f1' : undefined,
                                                    color: isSelected ? 'white' : hasSchedule ? '#006a61' : undefined,
                                                    fontWeight: hasSchedule ? 600 : undefined,
                                                    '&:hover': {
                                                        bgcolor: isSelected ? '#005a52' : hasSchedule ? '#cce8e4' : undefined,
                                                    },
                                                }}
                                            />
                                        );
                                    },
                                }}
                            />
                        </Box>

                        {currentDate && (
                            <>
                                <Typography variant="body2" color="text.secondary">
                                    {currentDate.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
                                </Typography>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <TextField
                                        type="time"
                                        size="small"
                                        value={timeInput}
                                        onChange={e => setTimeInput(e.target.value)}
                                        sx={{ flex: 1 }}
                                        inputProps={{ step: 60 }}
                                    />
                                    <LoadingButton
                                        variant="outlined"
                                        onClick={addSchedule}
                                        disabled={!timeInput}
                                        sx={{
                                            textTransform: 'none',
                                            borderColor: '#006a61',
                                            color: '#006a61',
                                            '&:hover': { borderColor: '#005a52', bgcolor: '#f0faf9' },
                                        }}
                                    >
                                        Adicionar
                                    </LoadingButton>
                                </Stack>

                                {currentDaySchedules.length > 0 ? (
                                    <Stack direction="row" gap={1} flexWrap="wrap">
                                        {currentDaySchedules.map(dt => (
                                            <Chip
                                                key={dt}
                                                label={new Date(dt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                onDelete={() => {
                                                    formik.setFieldValue('meeting_schedule', scheduledDates.filter(d => d !== dt));
                                                }}
                                                sx={{
                                                    bgcolor: '#e6f3f1',
                                                    color: '#006a61',
                                                    fontWeight: 500,
                                                    '& .MuiChip-deleteIcon': { color: '#006a61' },
                                                }}
                                            />
                                        ))}
                                    </Stack>
                                ) : (
                                    <Typography variant="caption" color="text.secondary">
                                        Nenhum horário neste dia
                                    </Typography>
                                )}
                            </>
                        )}

                        {totalSchedules > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 1, borderTop: '1px solid #f0f0f0' }}>
                                <Typography variant="body2" color="text.secondary">
                                    Total:
                                </Typography>
                                <Chip
                                    label={`${totalSchedules} horario${totalSchedules > 1 ? 's' : ''} definido${totalSchedules > 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 }}
                                />
                            </Box>
                        )}
                    </Stack>
                </SectionCard>

                {/* Submit */}
                <LoadingButton
                    type="submit"
                    fullWidth
                    loading={formik.isSubmitting}
                    variant="contained"
                    disabled={
                        !formik.values.title ||
                        !formik.values.description ||
                        scheduledDates.length === 0 ||
                        formik.isSubmitting ||
                        isOffline ||
                        (!isFreeEvent && formik.values.pricing <= 0)
                    }
                    sx={{
                        bgcolor: '#006a61',
                        borderRadius: '8px',
                        height: 48,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        '&:hover': { bgcolor: '#005a52' },
                    }}
                >
                    {scheduledDates.length === 0 ? 'Adicione pelo menos um horário' : 'Criar encontro'}
                </LoadingButton>

                {/* E aqui que o anfitriao decide o conteudo do encontro, entao
                    e aqui que a politica que restringe esse conteudo precisa
                    estar ao alcance -- ate agora ela era citada nos Termos sem
                    ter onde ser lida. */}
                <Typography variant="caption" color="text.secondary" textAlign="center">
                    Ao publicar, você se responsabiliza pelo conteúdo do encontro. Veja o que não pode
                    ser oferecido na{' '}
                    <Link
                        component={RouterLink}
                        to="/politicas/uso-responsavel"
                        target="_blank"
                        rel="noopener"
                        sx={{ color: '#006a61', fontWeight: 600 }}
                    >
                        Política Geral de Uso Responsável
                    </Link>
                    .
                </Typography>
            </form>

            <ConflictDialog
                open={conflictOpen}
                onClose={() => { setConflictOpen(false); setPendingScheduleISO(null); }}
                conflicts={conflictData}
                onResolved={() => {
                    if (pendingScheduleISO) finalizeAddSchedule(pendingScheduleISO);
                }}
            />

            <Dialog
                open={showSuccess}
                PaperProps={{ sx: { borderRadius: '16px', maxWidth: 420 } }}
            >
                <DialogContent sx={{ textAlign: 'center', p: { xs: 3, sm: 4 } }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                        Encontro criado com sucesso!
                    </Typography>
                    {!isApproved ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                            Seu encontro foi criado, porém ele só ficará visível para outros usuários
                            após a aprovação do seu cadastro de anfitrião, que está em análise.
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                            Seu encontro já está disponível para que outros usuários possam visualizar e adquirir.
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={() => navigation('/meus-encontros', { replace: true })}
                        sx={{
                            bgcolor: '#006a61',
                            borderRadius: '8px',
                            height: 44,
                            textTransform: 'none',
                            fontWeight: 600,
                            '&:hover': { bgcolor: '#005a52' },
                        }}
                    >
                        Ver meus encontros
                    </Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
};
